import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/cn";

export const alertVariants = cva(
  "relative grid w-full grid-cols-[0_1fr] items-start gap-y-0.5 rounded-lg border px-4 py-3 text-sm has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] has-[>svg]:gap-x-3 [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "bg-card text-destructive *:data-[slot=alert-description]:text-destructive/90 [&>svg]:text-current",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export type AlertProps = React.ComponentProps<"div"> & VariantProps<typeof alertVariants>;
/** A scoped adaptation of shadcn's current new-york-v4 alert primitive. */
export const Alert = ({ className, variant, ...props }: AlertProps) => (
  <div
    className={cn(alertVariants({ variant }), className)}
    data-slot="alert"
    role="alert"
    {...props}
  />
);

export type AlertTitleProps = React.ComponentProps<"div">;
/** Single-line heading of an {@link Alert}. */
export const AlertTitle = ({ className, ...props }: AlertTitleProps) => (
  <div
    className={cn("col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight", className)}
    data-slot="alert-title"
    {...props}
  />
);

export type AlertDescriptionProps = React.ComponentProps<"div">;
/** Supporting copy of an {@link Alert}. */
export const AlertDescription = ({ className, ...props }: AlertDescriptionProps) => (
  <div
    className={cn(
      "col-start-2 grid justify-items-start gap-1 text-sm text-muted-foreground [&_p]:leading-relaxed",
      className,
    )}
    data-slot="alert-description"
    {...props}
  />
);
